import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getPatternTitle } from './patternTutorialData';
import { fetchPatternImageAsBlob, fetchScannerDetail } from './scannerExperienceApi';
import {
  SCANNER_DETAIL_TIMEFRAMES,
  clampSummaryText,
  buildValidationMatrix,
  formatConfidence,
  formatDateTime,
  formatHistoryDate,
  formatPercent,
  formatPrice,
  formatProbability,
  formatRatio,
  formatRiskReward,
  normalizeText,
  resolveAlignmentDescriptor,
  resolveConvictionLabel,
  resolveConvictionScore,
  resolveDirectionDescriptor,
  resolveRiskDescriptor,
  resolveTradeDirectionDescriptor,
  resolveVolumeDescriptor,
} from './scannerExperienceShared';

function buildDetailSections(screen) {
  const pattern = screen?.pattern || {};
  return [
    {
      title: 'Structure',
      items: [
        ['Pattern', getPatternTitle(pattern.patternDetected || '')],
        ['Biais', resolveDirectionDescriptor(pattern.direction).label],
        ['Confiance', formatConfidence(pattern.confidence)],
        ['Résolution', normalizeText(pattern.timeToResolution, 'n/a')],
      ],
    },
    {
      title: 'Volume',
      items: [
        ['État', resolveVolumeDescriptor(pattern).label],
        ['Commentaire', normalizeText(pattern.volumeComment, 'n/a')],
        ['Ratio breakout', Number.isFinite(pattern.breakoutVolumeRatio) ? pattern.breakoutVolumeRatio.toFixed(2) : 'n/a'],
      ],
    },
    {
      title: 'Momentum',
      items: [
        ['État', normalizeText(pattern.momentum?.state || pattern.momentumState, 'n/a')],
        ['Force', Number.isFinite(pattern.momentum?.strength) ? `${Math.round(pattern.momentum.strength * 100)}%` : 'n/a'],
        ['Commentaire', normalizeText(pattern.momentum?.comment, 'n/a')],
      ],
    },
    {
      title: 'Divergence',
      items: [
        ['Type', normalizeText(pattern.divergence?.type || pattern.divergenceType, 'n/a')],
        ['Force', Number.isFinite(pattern.divergence?.strength) ? `${Math.round(pattern.divergence.strength * 100)}%` : 'n/a'],
        ['Commentaire', normalizeText(pattern.divergence?.comment, 'n/a')],
      ],
    },
    {
      title: 'Convergence',
      items: [
        ['État fusion', normalizeText(pattern.signalFusion?.state || pattern.signalFusionState, 'n/a')],
        ['Conviction', String(resolveConvictionScore(screen))],
        ['Narrative IA', normalizeText(pattern.signalFusion?.aiNarrative, normalizeText(pattern.aiNarrative, 'n/a'))],
      ],
    },
    {
      title: 'Candlestick',
      items: [
        ['Pattern', normalizeText(pattern.candlestick?.pattern || pattern.candlestickPattern, 'n/a')],
        ['Direction', normalizeText(pattern.candlestick?.direction, 'n/a')],
        ['Commentaire', normalizeText(pattern.candlestick?.comment, 'n/a')],
      ],
    },
  ];
}

function SummaryTile({ label, value, helper, tone, loading = false }) {
  return (
    <Paper elevation={0} sx={{ p: 1.8, borderRadius: 3.5, background: '#fffdf8', border: '1px solid rgba(16,37,43,0.08)' }}>
      <Typography variant="overline" sx={{ color: '#68777d', letterSpacing: '0.08em' }}>{label}</Typography>
      {loading ? (
        <Stack spacing={0.6} sx={{ mt: 0.5 }}>
          <Skeleton variant="text" animation="wave" width="68%" height={40} />
          <Skeleton variant="text" animation="wave" width="92%" height={22} />
        </Stack>
      ) : (
        <>
          <Typography variant="h5" sx={{ color: tone, fontWeight: 900, mb: 0.4 }}>{value}</Typography>
          <Typography variant="body2" sx={{ color: '#5c6d74', lineHeight: 1.65 }}>{helper}</Typography>
        </>
      )}
    </Paper>
  );
}

function SectionLoadingNotice({ message }) {
  return (
    <Alert severity="info" variant="outlined" sx={{ borderRadius: 2.8 }}>
      {message}
    </Alert>
  );
}

function buildExecutionPlan(pattern) {
  return [
    {
      label: 'Cassure',
      value: formatPrice(pattern?.breakoutLevel),
      helper: 'Niveau à valider pour activer le setup.',
    },
    {
      label: 'Cible',
      value: formatPrice(pattern?.targetPrice),
      helper: 'Projection technique du mouvement.',
    },
    {
      label: 'Invalidation',
      value: formatPrice(pattern?.stopInvalidation),
      helper: 'Zone où le scénario perd sa validité.',
    },
    {
      label: 'R/R',
      value: formatRatio(pattern?.riskRewardRatio),
      helper: 'Ratio rendement / risque du plan.',
    },
    {
      label: 'Confiance cible',
      value: formatPercent(pattern?.targetConfidence),
      helper: 'Probabilité de rejoindre la projection.',
    },
    {
      label: 'Résolution',
      value: normalizeText(pattern?.timeToResolution, 'n/a'),
      helper: 'Horizon tactique estimé.',
    },
  ];
}

function buildTradePlanHighlights(tradePlan) {
  if (!tradePlan) {
    return [];
  }

  return [
    {
      label: 'Trade Grade',
      value: normalizeText(tradePlan.tradeGrade, 'n/a'),
      helper: normalizeText(tradePlan.gradeComment, 'Qualité du setup selon structure, volume, momentum et contexte MTF.'),
    },
    {
      label: 'Conviction',
      value: Number.isFinite(tradePlan.conviction) ? `${tradePlan.conviction}` : 'n/a',
      helper: normalizeText(tradePlan.displayEligible ? 'Le scénario dépasse le seuil d’activation.' : tradePlan.displayMessage, 'Lecture du moteur indisponible.'),
    },
    {
      label: 'RR estimé',
      value: formatRiskReward(tradePlan.riskReward),
      helper: 'Ratio calculé sur l’entrée médiane, le stop robuste et le TP principal.',
    },
    {
      label: 'Probabilité',
      value: formatProbability(tradePlan.probability),
      helper: 'Lecture probabiliste du scénario principal, jamais une certitude.',
    },
  ];
}

function buildTradePlanReasoning(tradePlan) {
  const reasoning = tradePlan?.reasoning || {};
  return [
    ['Pourquoi cette direction ?', normalizeText(reasoning.whyDirection, 'n/a')],
    ['Pourquoi ce stop ?', normalizeText(reasoning.whyStop, 'n/a')],
    ['Pourquoi ces objectifs ?', normalizeText(reasoning.whyTargets, 'n/a')],
    ['Pourquoi ce levier ?', normalizeText(reasoning.whyLeverage, 'n/a')],
    ['Pourquoi ce RR ?', normalizeText(reasoning.whyRiskReward, 'n/a')],
  ];
}

const PatternScannerDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { symbol } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const exchange = searchParams.get('exchange') || 'BINANCE';
  const timeframe = searchParams.get('timeframe') || '4H';
  const [screen, setScreen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageLoading, setImageLoading] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadScreen = async () => {
      if (!symbol) {
        return;
      }
      setLoading(true);
      setError('');
      setScreen(null);
      setImageUrl('');
      setImageFailed(false);
      setImageLoading(true);
      try {
        const payload = await fetchScannerDetail(symbol, { exchange, timeframe });
        if (!cancelled) {
          setScreen(payload);
        }
      } catch (requestError) {
        if (!cancelled) {
          setScreen(null);
          setError('L’analyse détaillée est indisponible pour cet actif.');
          setImageLoading(false);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadScreen();
    return () => {
      cancelled = true;
    };
  }, [exchange, symbol, timeframe]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = '';
    let retryTimer = null;

    const analysis = screen?.pattern;
    if (!analysis?.symbol) {
      setImageUrl('');
      setImageFailed(false);
      setImageLoading(false);
      return () => {};
    }

    const loadImage = async (attempt = 0, forceSymbolLookup = false) => {
      setImageLoading(true);
      const controller = new AbortController();
      const abortTimer = window.setTimeout(() => controller.abort(), 5000);

      try {
        const blob = await fetchPatternImageAsBlob(analysis, { forceSymbolLookup, signal: controller.signal });
        if (blob && !cancelled) {
          objectUrl = URL.createObjectURL(blob);
          setImageUrl(objectUrl);
          setImageFailed(false);
          setImageLoading(false);
          return;
        }
      } catch (requestError) {
      } finally {
        window.clearTimeout(abortTimer);
      }

      if (!cancelled && attempt < 15) {
        retryTimer = window.setTimeout(() => {
          void loadImage(attempt + 1, true);
        }, 2000);
      } else if (!cancelled) {
        setImageUrl('');
        setImageFailed(true);
        setImageLoading(false);
      }
    };

    setImageFailed(false);
    setImageUrl('');
    void loadImage(0, !analysis.imagePath);

    return () => {
      cancelled = true;
      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [screen]);

  const currentPattern = screen?.pattern;
  const currentSummary = screen?.summary;
  const tradePlan = screen?.tradePlan;
  const convictionScore = resolveConvictionScore(screen || currentPattern || {});
  const convictionLabel = currentSummary?.convictionLabel || resolveConvictionLabel(convictionScore);
  const risk = resolveRiskDescriptor(currentPattern || {});
  const direction = resolveDirectionDescriptor(currentSummary?.bias || currentPattern?.direction);
  const tradeDirection = resolveTradeDirectionDescriptor(tradePlan?.direction);
  const alignment = resolveAlignmentDescriptor(screen || {});
  const multiTimeframeInsights = screen?.multiTimeframe?.insights;
  const multiTimeframeWarnings = screen?.multiTimeframe?.warnings || [];
  const validationMatrix = useMemo(() => buildValidationMatrix(screen), [screen]);
  const detailSections = useMemo(() => buildDetailSections(screen), [screen]);
  const executionPlan = useMemo(() => buildExecutionPlan(currentPattern), [currentPattern]);
  const tradePlanHighlights = useMemo(() => buildTradePlanHighlights(tradePlan), [tradePlan]);
  const tradePlanReasoning = useMemo(() => buildTradePlanReasoning(tradePlan), [tradePlan]);
  const multiTimeframeTabs = screen?.multiTimeframe?.timeframes || [];
  const selectedReplay = multiTimeframeTabs.find((entry) => entry?.analysis?.timeframe === timeframe);
  const returnTo = location.state?.returnTo || `/scanner?exchange=${encodeURIComponent(exchange)}&timeframe=${encodeURIComponent(timeframe)}`;
  const isInitialScreenLoading = loading && !screen;
  const isRefreshingScreen = loading && !!screen;
  const isGraphPending = isInitialScreenLoading || (Boolean(currentPattern?.symbol) && !imageUrl && !imageFailed);

  const updateTimeframe = (_, nextTimeframe) => {
    if (!nextTimeframe) {
      return;
    }
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('timeframe', nextTimeframe);
    setSearchParams(nextSearchParams, { replace: true });
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'radial-gradient(circle at top left, rgba(243,198,92,0.16), transparent 28%), linear-gradient(180deg, #fbf7ee 0%, #f2ece0 100%)', py: { xs: 4, md: 6 } }}>
      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.6} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
            <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate(returnTo)} sx={{ textTransform: 'none', fontWeight: 800 }}>
              Retour au scanner
            </Button>
            <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
              <Chip label={`${exchange} · ${timeframe}`} sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }} />
              <Chip label={isInitialScreenLoading ? 'Analyse en cours…' : `Analyse ${formatDateTime(currentPattern?.analysisTimestamp)}`} sx={{ bgcolor: '#f5efe2', color: '#17323c', fontWeight: 700 }} />
            </Stack>
          </Stack>

          {loading ? <LinearProgress sx={{ height: 8, borderRadius: 999 }} /> : null}
          {error ? <Alert severity="warning">{error}</Alert> : null}

          <Paper elevation={0} sx={{ p: { xs: 2.4, md: 3 }, borderRadius: 5, background: 'linear-gradient(135deg, #17323c 0%, #264653 100%)', color: '#fff8eb' }}>
            <Stack spacing={1.2}>
              <Typography variant="h3" sx={{ fontWeight: 900 }}>{isInitialScreenLoading ? 'Chargement de l’analyse…' : normalizeText(currentSummary?.symbol || symbol, 'Actif')}</Typography>
              {isInitialScreenLoading ? (
                <Typography variant="body1" sx={{ color: 'rgba(255,248,235,0.88)', lineHeight: 1.7, maxWidth: 720 }}>
                  Les signaux, niveaux et indicateurs détaillés de cette fenêtre sont en cours de chargement.
                </Typography>
              ) : (
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={getPatternTitle(currentSummary?.pattern || currentPattern?.patternDetected || '')} sx={{ bgcolor: 'rgba(255,248,235,0.12)', color: '#fff8eb', fontWeight: 800 }} />
                  <Chip label={direction.label} sx={{ bgcolor: direction.background, color: direction.tone, fontWeight: 800 }} />
                  <Chip label={convictionLabel} sx={{ bgcolor: '#f3c65c', color: '#17323c', fontWeight: 900 }} />
                  <Chip label={`Conviction ${convictionScore}`} sx={{ bgcolor: 'rgba(255,248,235,0.12)', color: '#fff8eb', fontWeight: 800 }} />
                </Stack>
              )}
            </Stack>
          </Paper>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1.2 }}>
            <SummaryTile label="Pattern" value={getPatternTitle(currentPattern?.patternDetected || '')} helper="Structure dominante détectée sur la fenêtre active." tone="#17323c" loading={isInitialScreenLoading} />
            <SummaryTile label="Biais" value={direction.label} helper="Direction tactique la plus cohérente à ce stade." tone={direction.tone} loading={isInitialScreenLoading} />
            <SummaryTile label="Conviction" value={`${convictionScore} / 100`} helper={convictionLabel} tone="#214f77" loading={isInitialScreenLoading} />
            <SummaryTile label="Risque" value={risk.label} helper="Lecture combinée du fakeout, du R/R et de la cible." tone={risk.tone} loading={isInitialScreenLoading} />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 3fr) minmax(300px, 1fr)' }, gap: 1.6 }}>
            <Paper component="section" aria-label="Graph principal" elevation={0} sx={{ p: 2.2, borderRadius: 4, border: '1px solid rgba(16,37,43,0.08)' }}>
              <Stack spacing={1.4}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                  <Typography variant="h5" sx={{ color: '#17323c', fontWeight: 900 }}>Graph principal</Typography>
                  {isInitialScreenLoading ? (
                    <Chip label="Niveaux en cours de chargement" sx={{ bgcolor: '#f5efe2', color: '#17323c', fontWeight: 800 }} />
                  ) : (
                    <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                      <Chip label={`Cassure ${formatPrice(currentPattern?.breakoutLevel)}`} sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }} />
                      <Chip label={`Cible ${formatPrice(currentPattern?.targetPrice)}`} sx={{ bgcolor: 'rgba(31,122,77,0.12)', color: '#1f7a4d', fontWeight: 800 }} />
                      <Chip label={`Invalidation ${formatPrice(currentPattern?.stopInvalidation)}`} sx={{ bgcolor: 'rgba(170,61,51,0.12)', color: '#aa3d33', fontWeight: 800 }} />
                    </Stack>
                  )}
                </Stack>

                {isRefreshingScreen ? (
                  <SectionLoadingNotice message={`Actualisation du graph ${timeframe} en cours. Les niveaux et le visuel se mettent à jour.`} />
                ) : null}

                <Tabs
                  value={timeframe}
                  onChange={updateTimeframe}
                  variant="scrollable"
                  allowScrollButtonsMobile
                  aria-label="Changer de timeframe du graph principal"
                  sx={{
                    minHeight: 44,
                    '& .MuiTab-root': {
                      minHeight: 44,
                      fontWeight: 800,
                      textTransform: 'none',
                    },
                  }}
                >
                  {SCANNER_DETAIL_TIMEFRAMES.map((entry) => (
                    <Tab key={entry} value={entry} label={entry} />
                  ))}
                </Tabs>

                <Box sx={{ minHeight: 460, borderRadius: 3.5, overflow: 'hidden', background: 'linear-gradient(180deg, rgba(245,239,226,0.9) 0%, rgba(255,255,255,0.98) 100%)', border: '1px solid rgba(16,37,43,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {isGraphPending ? (
                    <Stack spacing={1.2} alignItems="center">
                      <LinearProgress sx={{ width: 220, height: 8, borderRadius: 999 }} />
                      <Typography variant="body2" sx={{ color: '#5c6d74', fontWeight: 700 }}>Données du bloc en cours de chargement…</Typography>
                      <Typography variant="body2" sx={{ color: '#5c6d74', textAlign: 'center', maxWidth: 460 }}>
                        {isInitialScreenLoading
                          ? 'Le graphique principal, les annotations et les niveaux sont en préparation.'
                          : 'Le visuel annoté est en cours de génération ou de chargement pour cette fenêtre.'}
                      </Typography>
                    </Stack>
                  ) : imageUrl ? (
                    <Box component="img" src={imageUrl} alt={`Graph pattern ${symbol}`} sx={{ width: '100%', height: '100%', objectFit: 'contain', background: '#fff' }} />
                  ) : (
                    <Stack spacing={1.2} alignItems="center" sx={{ px: 3 }}>
                      <ShowChartRoundedIcon sx={{ fontSize: 54, color: '#8d6c16' }} />
                      <Typography variant="h6" sx={{ color: '#17323c', fontWeight: 800 }}>Visuel annoté indisponible</Typography>
                      <Typography variant="body2" sx={{ color: '#5c6d74', textAlign: 'center', maxWidth: 460 }}>
                        {imageFailed
                          ? 'Le moteur n’a pas encore exposé une image lisible pour cette fenêtre. Les niveaux et la lecture détaillée restent disponibles ci-dessous.'
                          : 'Le graphique annoté sera affiché dès que la génération aura terminé.'}
                      </Typography>
                    </Stack>
                  )}
                </Box>
              </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: 2.2, borderRadius: 4, border: '1px solid rgba(16,37,43,0.08)' }}>
              <Stack spacing={1.5}>
                <Typography variant="h6" sx={{ color: '#17323c', fontWeight: 900 }}>Panneau contexte</Typography>
                {loading ? (
                  <SectionLoadingNotice
                    message={isInitialScreenLoading
                      ? 'Contexte, validations et plan d’exécution en cours de chargement.'
                      : `Actualisation du contexte ${timeframe} en cours.`}
                  />
                ) : null}

                {!screen ? null : (
                  <>
                <Chip label={`Conviction ${convictionScore} · ${convictionLabel}`} sx={{ alignSelf: 'flex-start', bgcolor: '#17323c', color: '#fff8eb', fontWeight: 900 }} />

                <Box sx={{ display: 'grid', gap: 0.9 }}>
                  {validationMatrix.map((item) => (
                    <Paper key={item.label} elevation={0} sx={{ p: 1.2, borderRadius: 2.8, background: '#fffdf8', border: '1px solid rgba(16,37,43,0.06)' }}>
                      <Stack direction="row" justifyContent="space-between" spacing={1.5}>
                        <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800 }}>{item.label}</Typography>
                        <Typography variant="subtitle2" sx={{ color: item.passed ? '#1f7a4d' : '#aa3d33', fontWeight: 900 }}>{item.passed ? '✓' : '✗'}</Typography>
                      </Stack>
                      <Typography variant="caption" sx={{ color: '#5d6e75', display: 'block', mt: 0.45, lineHeight: 1.6 }}>{item.helper}</Typography>
                    </Paper>
                  ))}
                </Box>

                <Divider />

                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800, mb: 0.8 }}>Plan d'exécution</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.9 }}>
                    {executionPlan.map((item) => (
                      <Paper key={item.label} elevation={0} sx={{ p: 1.1, borderRadius: 2.8, background: '#fffdf8', border: '1px solid rgba(16,37,43,0.06)' }}>
                        <Typography variant="caption" sx={{ color: '#6b7a80', letterSpacing: '0.04em' }}>{item.label}</Typography>
                        <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 900, mt: 0.25 }}>{item.value}</Typography>
                        <Typography variant="caption" sx={{ color: '#5d6e75', display: 'block', mt: 0.45, lineHeight: 1.6 }}>{item.helper}</Typography>
                      </Paper>
                    ))}
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800, mb: 0.8 }}>Dominante multi-timeframe</Typography>
                  <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mb: 0.9 }}>
                    <Chip label={`Alignement ${Number.isFinite(multiTimeframeInsights?.alignmentScore) ? multiTimeframeInsights.alignmentScore : 'n/a'}`} sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }} />
                    <Chip label={`Conviction MTF ${Number.isFinite(multiTimeframeInsights?.convictionScore) ? multiTimeframeInsights.convictionScore : 'n/a'}`} sx={{ bgcolor: '#f5efe2', color: '#17323c', fontWeight: 800 }} />
                    <Chip label={`Volume ${Number.isFinite(multiTimeframeInsights?.volumeAlignmentScore) ? multiTimeframeInsights.volumeAlignmentScore : 'n/a'}`} sx={{ bgcolor: 'rgba(31,122,77,0.12)', color: '#1f7a4d', fontWeight: 800 }} />
                  </Stack>
                  <Typography variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.7 }}>
                    {normalizeText(
                      multiTimeframeInsights?.multiTfNarrative,
                      `Direction dominante ${normalizeText(multiTimeframeInsights?.dominantDirection, 'n/a')} sur ${normalizeText(multiTimeframeInsights?.dominantTimeframe, 'n/a')}.`,
                    )}
                  </Typography>
                  {multiTimeframeWarnings.length > 0 ? (
                    <Alert severity="warning" sx={{ mt: 1.1 }}>
                      {multiTimeframeWarnings.join(' · ')}
                    </Alert>
                  ) : null}
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800, mb: 0.45 }}>Risque principal</Typography>
                  <Typography variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.7 }}>
                    {normalizeText(screen?.graph?.volumeComment, clampSummaryText(currentPattern?.aiSummary))}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800, mb: 0.45 }}>Résumé IA</Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#5d6e75',
                      lineHeight: 1.7,
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 3,
                      overflow: 'hidden',
                    }}
                  >
                    {clampSummaryText(currentSummary?.aiSummary || currentPattern?.aiSummary)}
                  </Typography>
                </Box>

                <Chip label={`Alignement MTF ${alignment.label}`} sx={{ alignSelf: 'flex-start', bgcolor: alignment.background, color: alignment.tone, fontWeight: 800 }} />
                  </>
                )}
              </Stack>
            </Paper>
          </Box>

          <Paper component="section" aria-label="Trade Plan" elevation={0} sx={{ p: 2.2, borderRadius: 4, border: '1px solid rgba(16,37,43,0.08)', background: 'linear-gradient(180deg, rgba(255,253,248,0.96) 0%, rgba(248,244,236,1) 100%)' }}>
            <Stack spacing={1.5}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.1} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                <Box>
                  <Typography variant="h5" sx={{ color: '#17323c', fontWeight: 900 }}>Trade Plan</Typography>
                  <Typography variant="body2" sx={{ color: '#5c6d74', lineHeight: 1.7, mt: 0.4 }}>
                    Le moteur reste probabiliste et n’émet jamais un ordre d’achat ou de vente, seulement le scénario actuellement le plus robuste.
                  </Typography>
                </Box>
                {tradePlan ? (
                  <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                    <Chip label={tradeDirection.label} sx={{ bgcolor: tradeDirection.background, color: tradeDirection.tone, fontWeight: 900 }} />
                    {tradePlan.displayEligible ? (
                      <>
                        <Chip label={`Trade Grade ${normalizeText(tradePlan.tradeGrade, 'n/a')}`} sx={{ bgcolor: '#17323c', color: '#fff8eb', fontWeight: 800 }} />
                        <Chip label={`RR ${formatRiskReward(tradePlan.riskReward)}`} sx={{ bgcolor: '#f3c65c', color: '#17323c', fontWeight: 900 }} />
                      </>
                    ) : (
                      <Chip label="Aucun trade suggéré" sx={{ bgcolor: '#f5efe2', color: '#17323c', fontWeight: 800 }} />
                    )}
                  </Stack>
                ) : null}
              </Stack>

              {loading && !tradePlan ? (
                <SectionLoadingNotice message="Trade plan en cours de génération." />
              ) : null}

              {tradePlan ? (
                <>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' }, gap: 0.9 }}>
                    {tradePlanHighlights.map((item) => (
                      <Paper key={item.label} elevation={0} sx={{ p: 1.2, borderRadius: 2.8, background: '#fff', border: '1px solid rgba(16,37,43,0.06)' }}>
                        <Typography variant="caption" sx={{ color: '#6b7a80', letterSpacing: '0.04em' }}>{item.label}</Typography>
                        <Typography variant="subtitle1" sx={{ color: '#17323c', fontWeight: 900, mt: 0.25 }}>{item.value}</Typography>
                        <Typography variant="caption" sx={{ color: '#5d6e75', display: 'block', mt: 0.45, lineHeight: 1.6 }}>{item.helper}</Typography>
                      </Paper>
                    ))}
                  </Box>

                  {tradePlan.displayEligible ? (
                    <>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.3fr) minmax(0, 1fr)' }, gap: 1.2 }}>
                        <Paper elevation={0} sx={{ p: 1.6, borderRadius: 3.2, background: '#fff', border: '1px solid rgba(16,37,43,0.06)' }}>
                          <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 900, mb: 0.9 }}>Zone d’entrée</Typography>
                          <Stack spacing={0.7}>
                            <Typography variant="h6" sx={{ color: '#17323c', fontWeight: 900 }}>
                              {formatPrice(tradePlan.entry?.min)} → {formatPrice(tradePlan.entry?.max)}
                            </Typography>
                            <Chip label={normalizeText(tradePlan.entry?.type, 'n/a').replaceAll('_', ' ')} sx={{ alignSelf: 'flex-start', bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }} />
                            <Typography variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.7 }}>{normalizeText(tradePlan.entry?.reason, 'n/a')}</Typography>
                          </Stack>
                        </Paper>

                        <Paper elevation={0} sx={{ p: 1.6, borderRadius: 3.2, background: '#fff', border: '1px solid rgba(16,37,43,0.06)' }}>
                          <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 900, mb: 0.9 }}>Stop d’invalidation</Typography>
                          <Stack spacing={0.7}>
                            <Typography variant="h6" sx={{ color: '#aa3d33', fontWeight: 900 }}>{formatPrice(tradePlan.stopLoss?.price)}</Typography>
                            <Typography variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.7 }}>
                              Distance {Number.isFinite(tradePlan.stopLoss?.distancePercent) ? `${tradePlan.stopLoss.distancePercent.toFixed(1)} %` : 'n/a'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.7 }}>{normalizeText(tradePlan.stopLoss?.reason, 'n/a')}</Typography>
                          </Stack>
                        </Paper>
                      </Box>

                      <Box>
                        <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 900, mb: 0.8 }}>Objectifs</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }, gap: 0.9 }}>
                          {(tradePlan.takeProfits || []).map((target) => (
                            <Paper key={target.label} elevation={0} sx={{ p: 1.2, borderRadius: 2.8, background: '#fff', border: '1px solid rgba(16,37,43,0.06)' }}>
                              <Typography variant="caption" sx={{ color: '#6b7a80', letterSpacing: '0.04em' }}>{target.label}</Typography>
                              <Typography variant="subtitle1" sx={{ color: '#17323c', fontWeight: 900, mt: 0.25 }}>{formatPrice(target.price)}</Typography>
                              <Typography variant="caption" sx={{ color: '#5d6e75', display: 'block', mt: 0.3 }}>RR {formatRiskReward(target.rr)}</Typography>
                              <Typography variant="caption" sx={{ color: '#5d6e75', display: 'block', mt: 0.15 }}>Probabilité {formatProbability(target.probability)}</Typography>
                              <Typography variant="caption" sx={{ color: '#5d6e75', display: 'block', mt: 0.45, lineHeight: 1.6 }}>{normalizeText(target.reason, 'n/a')}</Typography>
                            </Paper>
                          ))}
                        </Box>
                      </Box>

                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.1fr) minmax(0, 1fr)' }, gap: 1.2 }}>
                        <Paper elevation={0} sx={{ p: 1.6, borderRadius: 3.2, background: '#fff', border: '1px solid rgba(16,37,43,0.06)' }}>
                          <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 900, mb: 0.9 }}>Levier prudent conseillé</Typography>
                          <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mb: 0.9 }}>
                            {(tradePlan.recommendedLeverage?.allowedModes || []).map((mode) => (
                              <Chip key={mode} label={mode} sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }} />
                            ))}
                          </Stack>
                          <Typography variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.7 }}>
                            {normalizeText(tradePlan.reasoning?.whyLeverage, 'Le moteur limite volontairement le levier pour éviter les plans agressifs.')}
                          </Typography>
                        </Paper>

                        <Paper elevation={0} sx={{ p: 1.6, borderRadius: 3.2, background: '#fff', border: '1px solid rgba(16,37,43,0.06)' }}>
                          <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 900, mb: 0.9 }}>Scénario alternatif</Typography>
                          <Stack spacing={0.7}>
                            <Chip label={`${resolveTradeDirectionDescriptor(tradePlan.alternativeScenario?.direction).label} · ${formatProbability(tradePlan.alternativeScenario?.probability)}`} sx={{ alignSelf: 'flex-start', bgcolor: '#f5efe2', color: '#17323c', fontWeight: 800 }} />
                            <Typography variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.7 }}>{normalizeText(tradePlan.alternativeScenario?.condition, 'n/a')}</Typography>
                          </Stack>
                        </Paper>
                      </Box>

                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1.2 }}>
                        <Paper elevation={0} sx={{ p: 1.6, borderRadius: 3.2, background: '#fff', border: '1px solid rgba(16,37,43,0.06)' }}>
                          <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 900, mb: 0.9 }}>Conditions d’annulation</Typography>
                          <Stack spacing={0.65}>
                            {(tradePlan.cancellationConditions || []).map((item) => (
                              <Typography key={item} variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.7 }}>• {item}</Typography>
                            ))}
                          </Stack>
                        </Paper>

                        <Paper elevation={0} sx={{ p: 1.6, borderRadius: 3.2, background: '#fff', border: '1px solid rgba(16,37,43,0.06)' }}>
                          <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 900, mb: 0.9 }}>Trade Summary</Typography>
                          <Typography variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.8 }}>{clampSummaryText(tradePlan.summary)}</Typography>
                        </Paper>
                      </Box>

                      {(tradePlan.profiles || []).length > 0 ? (
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 900, mb: 0.8 }}>Profils de trade</Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 0.9 }}>
                            {tradePlan.profiles.map((profile) => (
                              <Paper key={profile.name} elevation={0} sx={{ p: 1.2, borderRadius: 2.8, background: '#fff', border: '1px solid rgba(16,37,43,0.06)' }}>
                                <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 900 }}>{profile.name}</Typography>
                                <Typography variant="caption" sx={{ color: '#6b7a80', display: 'block', mt: 0.3 }}>{normalizeText(profile.entryType, 'n/a').replaceAll('_', ' ')}</Typography>
                                <Typography variant="caption" sx={{ color: '#5d6e75', display: 'block', mt: 0.2 }}>RR {formatRiskReward(profile.riskReward)}</Typography>
                                <Typography variant="caption" sx={{ color: '#5d6e75', display: 'block', mt: 0.2 }}>Levier {normalizeText(profile.leverage, 'n/a')}</Typography>
                                <Typography variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.7, mt: 0.7 }}>{normalizeText(profile.description, 'n/a')}</Typography>
                              </Paper>
                            ))}
                          </Box>
                        </Box>
                      ) : null}
                    </>
                  ) : (
                    <Alert severity="info" sx={{ borderRadius: 2.8 }}>
                      {normalizeText(tradePlan.displayMessage, 'Aucun plan de trading proposé actuellement.')}
                    </Alert>
                  )}

                  <Accordion disableGutters elevation={0} sx={{ borderRadius: 3.2, border: '1px solid rgba(16,37,43,0.08)', overflow: 'hidden' }}>
                    <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ background: '#fff' }}>
                      <Typography variant="subtitle1" sx={{ color: '#17323c', fontWeight: 900 }}>Voir le raisonnement du moteur</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ background: '#fcfbf7' }}>
                      <Stack spacing={1.1}>
                        {tradePlanReasoning.map(([label, value]) => (
                          <Box key={label}>
                            <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800 }}>{label}</Typography>
                            <Typography variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.75, mt: 0.25 }}>{value}</Typography>
                          </Box>
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                </>
              ) : null}
            </Stack>
          </Paper>



          <Accordion disableGutters elevation={0} sx={{ borderRadius: 4, border: '1px solid rgba(16,37,43,0.08)', overflow: 'hidden' }}>
            <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ background: '#fff' }}>
              <Typography variant="h6" sx={{ color: '#17323c', fontWeight: 900 }}>Analyse détaillée</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ background: '#fcfbf7' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1.2 }}>
                {detailSections.map((section) => (
                  <Paper key={section.title} elevation={0} sx={{ p: 1.7, borderRadius: 3.2, background: '#fff', border: '1px solid rgba(16,37,43,0.06)' }}>
                    <Typography variant="subtitle1" sx={{ color: '#17323c', fontWeight: 900, mb: 1 }}>{section.title}</Typography>
                    <Stack spacing={0.9}>
                      {section.items.map(([label, value]) => (
                        <Box key={`${section.title}-${label}`}>
                          <Typography variant="caption" sx={{ color: '#6b7a80', letterSpacing: '0.04em' }}>{label}</Typography>
                          <Typography variant="body2" sx={{ color: '#41535b', lineHeight: 1.7 }}>{value}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>

          <Paper elevation={0} sx={{ p: 2.2, borderRadius: 4, border: '1px solid rgba(16,37,43,0.08)' }}>
            <Typography variant="h6" sx={{ color: '#17323c', fontWeight: 900, mb: 1.4 }}>Historique pattern</Typography>
            {loading ? (
              <SectionLoadingNotice
                message={isInitialScreenLoading
                  ? 'Historique du pattern en cours de chargement.'
                  : `Actualisation de l’historique ${timeframe} en cours.`}
              />
            ) : null}
            <Stack spacing={1.1}>
              {(screen?.history || []).slice(0, 6).map((entry) => (
                <Stack key={`${entry.symbol}-${entry.timeframe}-${entry.analysisTimestamp}`} direction="row" spacing={1.2} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f3c65c', flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: '#17323c', fontWeight: 700, minWidth: 64 }}>{formatHistoryDate(entry.analysisTimestamp)}</Typography>
                  <Typography variant="body2" sx={{ color: '#5d6e75' }}>{getPatternTitle(entry.patternDetected)}</Typography>
                </Stack>
              ))}
              {screen && (!screen.history || screen.history.length === 0) ? (
                <Alert severity="info">Aucun historique n’est encore disponible pour cette fenêtre.</Alert>
              ) : null}
            </Stack>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
};

export default PatternScannerDetailPage;
